import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { filledInputClasses } from '@mui/material/FilledInput';
import { styled } from '@mui/material/styles';
import { ContractorCompanyRate } from 'api/services/company/company-service.schema';
import { rateDraftKey, sanitizeRateInput } from './contractor-rate-values';
import { useContractorRateColumns } from './use-contractor-rate-columns';

type ContractorRatesEditTableProps = {
  jobTypes: ContractorCompanyRate[];
  drafts: Record<string, string>;
  disabled: boolean;
  onRateChange: (draftKey: string, value: string) => void;
};

const ContractorRatesEditTable = ({
  jobTypes,
  drafts,
  disabled,
  onRateChange,
}: ContractorRatesEditTableProps) => {
  const { t } = useTranslation('ogp-screen-specific');
  const { columns, jobTypeLabel, rateUnitLabel } = useContractorRateColumns();

  if (jobTypes.length === 0) {
    return <Typography>{t('company.contractor-rates.empty.description')}</Typography>;
  }

  return (
    <RatesTable data-testid="contractor-company-rates">
      <TableHead>
        <TableRow>
          <TableCell>{jobTypeLabel}</TableCell>
          {columns.map((column) => (
            <TableCell key={column.employmentType}>
              {column.label}
              <RateUnit variant="caption" color="text.secondary">
                {rateUnitLabel}
              </RateUnit>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {jobTypes.map((jobType) => (
          <TableRow key={jobType.jobTypeId}>
            <TableCell>{jobType.jobTypeName}</TableCell>
            {columns.map((column) => {
              const draftKey = rateDraftKey(jobType.jobTypeId, column.employmentType);
              const ariaLabel = t('company.contractor-rates.rate.aria-label', {
                jobType: jobType.jobTypeName,
                employmentType: column.label,
                unit: rateUnitLabel,
              });

              return (
                <TableCell key={column.employmentType}>
                  <RateField
                    value={drafts[draftKey] ?? ''}
                    onChange={(event) =>
                      onRateChange(draftKey, sanitizeRateInput(event.target.value))
                    }
                    disabled={disabled}
                    variant="filled"
                    size="small"
                    inputProps={{
                      'aria-label': ariaLabel,
                      inputMode: 'decimal',
                      'data-testid': `contractor-rate-${jobType.jobTypeId}-${column.employmentType}`,
                    }}
                  />
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </RatesTable>
  );
};

const RatesTable = styled(Table)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const RateUnit = styled(Typography)({
  display: 'block',
});

const RateField = styled(TextField)(({ theme }) => ({
  width: theme.spacing(18),
  [`& .${filledInputClasses.root}`]: {
    borderRadius: theme.spacing(2),
  },
}));

export { ContractorRatesEditTable };
